import { useState, type CSSProperties } from "react";
import { Pill, ConfirmModal, showToast } from "../primitives";
import {
  useWrappedApiMappings,
  useRemoveWrappedApiMapping,
  type WrappedApiMapping,
  type WrappedApiOperation,
} from "../../hooks/use-wrapped-apis";
import { MappingEditor } from "./MappingEditor";

/**
 * Sprint 16c (S16C.24) — the Mappings tab. Lists response→entity mappings for a
 * wrapper, one row per mapped operation, with the target type, sync mode, and
 * field/FK counts. Owners add, edit, and remove; members see them read-only.
 */
export function MappingsList({
  slug,
  operations,
  isOwner,
}: {
  slug: string;
  operations: WrappedApiOperation[];
  isOwner: boolean;
}) {
  const mappings = useWrappedApiMappings(slug);
  const removeMapping = useRemoveWrappedApiMapping();

  const [editing, setEditing] = useState<WrappedApiMapping | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<WrappedApiMapping | null>(null);

  const rows = mappings.data ?? [];

  async function handleRemove(m: WrappedApiMapping) {
    try {
      await removeMapping.mutateAsync({ wrapper_slug: slug, operation_id: m.operation_id });
      showToast("Mapping removed");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Couldn't remove", "error", true);
    }
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: "var(--color-text-secondary)", lineHeight: 1.5, maxWidth: 560 }}>
          Mappings materialise this API's responses into structured entity rows — FK'd to your native entities and
          captured as Activity. {isOwner ? "" : "An owner configures these."}
        </div>
        {isOwner && (
          <button type="button" onClick={() => setCreating(true)} style={newBtn}>
            <i className="ti ti-plus" style={{ fontSize: 13 }} aria-hidden="true" /> New mapping
          </button>
        )}
      </div>

      {mappings.isLoading ? (
        <div style={muted}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={emptyBox}>
          <i className="ti ti-arrow-merge" style={{ fontSize: 22, color: "var(--color-text-tertiary)" }} aria-hidden="true" />
          <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginTop: 8 }}>No mappings yet</div>
          <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginTop: 4, maxWidth: 420, lineHeight: 1.5 }}>
            {isOwner
              ? "Map an operation (e.g. listTickets) to an entity type to start ingesting its results into your workspace."
              : "An owner hasn't mapped any operations yet."}
          </div>
        </div>
      ) : (
        <div style={{ border: "var(--kuvio-border-default)", borderRadius: "var(--border-radius-sm)", overflow: "hidden" }}>
          {rows.map((m, i) => (
            <div key={m.id} style={{ ...row, borderTop: i === 0 ? "none" : "0.5px solid var(--color-border-secondary)" }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 13, fontFamily: "var(--font-mono)", color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {m.operation_id}
                </div>
                <div style={{ display: "flex", gap: 6, marginTop: 6, alignItems: "center", flexWrap: "wrap" }}>
                  <Pill variant="blue" icon="database">{m.target_entity_type_slug}</Pill>
                  <Pill variant={m.sync_mode === "live" ? "green" : m.sync_mode === "cache" ? "amber" : "purple"} icon="refresh">{m.sync_mode}</Pill>
                  <span style={countText}>{Object.keys(m.field_map).length} field{Object.keys(m.field_map).length === 1 ? "" : "s"}</span>
                  {m.fk_resolution.length > 0 && <span style={countText}>{m.fk_resolution.length} FK</span>}
                  {m.scope_filter && <span style={countText}>scoped</span>}
                </div>
              </div>
              {isOwner && (
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button type="button" onClick={() => setEditing(m)} style={iconBtn} aria-label="Edit mapping">
                    <i className="ti ti-pencil" style={{ fontSize: 14 }} aria-hidden="true" />
                  </button>
                  <button type="button" onClick={() => setConfirmRemove(m)} style={iconBtn} aria-label="Remove mapping">
                    <i className="ti ti-trash" style={{ fontSize: 14 }} aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {creating && (
        <MappingEditor slug={slug} operations={operations} onClose={() => setCreating(false)} />
      )}
      {editing && (
        <MappingEditor slug={slug} operations={operations} existing={editing} onClose={() => setEditing(null)} />
      )}
      <ConfirmModal
        open={!!confirmRemove}
        onOpenChange={(o) => { if (!o) setConfirmRemove(null); }}
        title="Remove mapping?"
        message={`Responses from "${confirmRemove?.operation_id}" will stop materialising. Already-materialised entities remain in the workspace.`}
        confirmLabel="Remove"
        variant="danger"
        onConfirm={async () => { if (confirmRemove) await handleRemove(confirmRemove); setConfirmRemove(null); }}
      />
    </div>
  );
}

const newBtn: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  padding: "6px 12px",
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  border: "none",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  flexShrink: 0,
};
const row: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  padding: "12px 14px",
  background: "var(--color-background-primary)",
};
const iconBtn: CSSProperties = {
  width: 30,
  height: 30,
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};
const countText: CSSProperties = { fontSize: 11, color: "var(--color-text-tertiary)" };
const muted: CSSProperties = { fontSize: 13, color: "var(--color-text-tertiary)", padding: "20px 0", textAlign: "center" };
const emptyBox: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  padding: "36px 20px",
  border: "0.5px dashed var(--color-border-secondary)",
  borderRadius: "var(--border-radius-md)",
};
